<template>
  <!-- Note that we use fill-height since it's annoying to have the dialog resize based on search results -->
  <dialog-scroll-lock content-class="fill-height" :value="value" width="500" v-bind="$attrs" v-on="$listeners">

    <error-dialog :status="loadProductsStatus" />
    <error-dialog :status="selectStatus" />
    <error-dialog :status="selectCountryStatus" />
    <error-dialog :status="searchPrivateStatus" />

    <!-- WARNING: this dialog has A LOT of overlap with the dialog in the catalog-gift-cards view -->
    <dialog-scroll-lock v-if="selected" width="400" :value="true" @input="selected = null">
      <v-card>
        <v-btn class="float-right mt-3 mr-3" icon @click="selected = null">
          <v-icon v-html="mdiClose" />
        </v-btn>
        <v-card-title class="pa-4">Specify amount</v-card-title>
        <v-card-subtitle v-if="(order.n_giftcard_recipients || 1) > 1" class="mt-n5 px-4 pb-6">
          This gift card will be sent to {{order.n_giftcard_recipients | pl('recipient')}}
        </v-card-subtitle>
        <v-card-subtitle class="px-4 mt-0">
          <v-card class="float-right ml-4 mb-1 gc-border" width="100">
            <v-img :aspect-ratio="337 / 213" :src="selected.img" />
          </v-card>
          <div class="overline short" style="color: rgba(0, 0, 0, 0.87);" v-html="selected.name" />
          <giftcard-details :src="selected.src" :product-id="selected.id" />
        </v-card-subtitle>
        <div style="clear: both;" />
        <div class="px-4 pb-4" v-bind:class="{ 'pt-4': selected.values.fixed }">
          <v-form v-model="amtValid" @submit.prevent="() => amtValid && select()">
            <giftcard-amount :cur="selected.cur" :allowed-values="selected.values" :max="maxValue" v-model="amt" />
          </v-form>
        </div>
        <template v-if="noAmtValid">
          <v-divider />
          <v-card-text class="error--text font-italic pa-4">Max gift card value is restricted to <currency :code="$options.filters.cur(country)" />{{order.skin.max_giftcard_value || 500000}} – there are no valid values that can be assigned to this gift card. Please try a different one.</v-card-text>
        </template>
        <template v-if="isCompleted">
          <v-divider />
          <payment
            class="py-4 mx-auto"
            style="min-height: 414px;"
            passive-sign-in
            :metadata="paymentMetadata"
            @complete="$emit('reload'); $emit('input', false);"
          />
        </template>
        <!-- TODO: you have a bug here where the form validation that sets the 'amtValid' property does not run properly - I think it has something to do with your 'selected' watcher. You can reproduce by selecting a gift card with a range of values, then selecting one with fixed values (where the previously entered range did not match), and you'll see the 'Add to card' button is initially disabled. -->
        <v-card-actions v-else class="pb-4">
          <v-spacer />
          <v-btn @click="selected = null">Cancel</v-btn>
          <v-btn color="primary" :disabled="!amtValid" @click="select">Add to card</v-btn>
        </v-card-actions>
      </v-card>
    </dialog-scroll-lock>

    <v-card class="d-flex flex-column fill-height">
      <v-card-title class="flex-grow-0 pa-4 d-block">
        <v-btn class="float-right mt-n1 mr-n1" icon @click="$emit('input', false)">
          <v-icon v-html="mdiClose" />
        </v-btn>
        <div>Add gift card</div>
        <div class="text-caption">
          <a href="/faq#how-do-gift-cards-work" target="_blank">How gift cards work</a>
        </div>
      </v-card-title>
      <v-divider class="flex-grow-0" />
      <div class="flex-grow-0 pa-4">
        <div class="d-flex align-center" style="width: 100%;">
          <v-text-field
            outlined dense rounded hide-details clearable
            label="Search"
            v-model="searchText"
            @click:clear="searchText = ''"
          />
          <v-menu offset-y max-height="90%" nudge-left="10">
            <template v-slot:activator="{ on }">
              <div v-on="on" class="d-flex align-center ml-2" style="cursor: pointer;">
                <country-flag :country="country" />
                <v-icon v-html="mdiMenuDown" />
              </div>
            </template>
            <v-list dense>
              <v-list-item
                v-for="code in SUPPORTED_COUNTRY_CODES" :key="code"
                :input-value="country == code"
                @click="selectCountry(code)"
              >
                <country-flag class="my-n1 ml-n1 mr-2 flex-shrink-0" style="transform: scale(.8);" :country="code" />
                <v-list-item-title class="overline">{{code | country-name}}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </div>
      <div v-if="USER_COUNTRY != country" class="flex-grow-0 caption font-italic warning--text px-4 pb-4">
        <v-icon class="mr-1" small color="warning" v-html="mdiAlert" /> These gift cards can only be redeemed in {{country | country-name}}
      </div>
      <div class="flex-grow-1 overflow-y-auto px-4">
        <div v-if="loadProductsStatus.pending" class="fill-height d-flex justify-center align-center">
          <v-progress-circular indeterminate />
        </div>
        <template v-else-if="products.length">
          <v-row>
            <v-col v-for="product in filteredProducts" :key="product.id" cols="6">
              <v-card class="gc-border" dark hover @click="selected = product">
                <v-img :aspect-ratio="300 / 190" :src="product.img" />
              </v-card>
              <div class="pt-1 overline short text-center">{{product.name}}</div>
            </v-col>
          </v-row>
          <div v-if="filteredProducts.length" class="mt-8 mb-3 caption">The merchants represented are not sponsors of the rewards or otherwise affiliated with Ellacard. The logos and other identifying marks attached are trademarks of and owned by each represented company and / or its affiliates. Please visit each company’s website for additional terms and conditions.</div>
          <div v-else class="mt-5 caption">
            No gift cards found

            <!-- This allows Ellacard admins to load private gift cards directly -->
            <div v-if="global.profile?.admin" class="mt-16">
              <div class="caption font-italic mb-1">Ellacard admins only</div>
              <v-btn small @click="searchPrivate">Search private gift cards…</v-btn>
            </div>
          </div>

        </template>
      </div>
    </v-card>
  </dialog-scroll-lock>
</template>


<style>
.no-margin-last-p > p:last-child {
  margin-bottom: 0;
}
</style>


<script>
import CountryFlag from '@/components/CountryFlag.vue';
import Currency from '@/components/Currency.vue';
import ErrorDialog from '@/components/ErrorDialog.vue';
import GiftcardAmount from '@/components/GiftcardAmount.vue';
import GiftcardDetails from '@/components/GiftcardDetails.vue';
import Payment from '@/components/Payment.vue';
import DialogScrollLockLoader from '@/components/DialogScrollLockLoader.vue';
import getCountryName from '@/filters/country-name.js';
import { SUPPORTED_COUNTRY_CODES, getFee } from '@/utils/giftcard.js';
import { CURRENCY_CODES, COUNTRY_CODE } from '@/utils/intl.js';
import uuid4 from '@/utils/uuid.js';
import { mdiAlert, mdiClose, mdiMenuDown, mdiInformation, mdiFileDocument } from '@mdi/js';


// For when a gift card has no description / disclosure
const DEFAULT_MSG = '<a href="/faq#how-do-gift-cards-work" target="_blank">How gift cards work</a>';


export default {
  name: 'giftcard-dialog',

  components: {
    CountryFlag,
    Currency,
    ErrorDialog,
    GiftcardAmount,
    GiftcardDetails,
    Payment,
    DialogScrollLockLoader
  },

  props: {
    value: Boolean,
    orderId: String,
    order: Object,
    selectedPageId: String,
    isCompleted: Boolean,
    maxValue: { type: Number, default: 500000 }
  },

  data() {
    return {
      defaultAmt: null,
      amt: '0',
      amtValid: false,
      noAmtValid: false,
      searchText: null,
      selected: null,
      products: [],
      country: SUPPORTED_COUNTRY_CODES.includes(COUNTRY_CODE) ? COUNTRY_CODE : 'US',
      paymentMetadata: null,
      loadProductsStatus: this.$track('loadProducts'),
      selectStatus: this.$track('select'),
      selectCountryStatus: this.$track('selectCountry'),
      searchPrivateStatus: this.$track('searchPrivate')
    };
  },

  computed: {
    searchTerms() {
      return (this.searchText || '').split(/\s+/).filter(x => !!x).map(x => x.toLowerCase());
    },

    existingCountry() {
      for (const page of this.order.pages)
        for (const blob of page.blobs)
          if (blob.type == 'GIFTCARD')
            return blob.giftcard.country;
      return null;
    },

    filteredProducts() {
      // RU:VISA-US
      if (this.searchTerms.length == 0)
        return this.products;
      return this.products.filter(x => {
        for (const term of this.searchTerms)
          if (x._nameLower.includes(term))
            return true;
          return false;
      });
    }
  },

  created() {
    Object.assign(this, { DEFAULT_MSG, USER_COUNTRY: COUNTRY_CODE, SUPPORTED_COUNTRY_CODES, mdiAlert, mdiClose, mdiMenuDown, mdiInformation, mdiFileDocument });
  },

  filters: {
    cur(country) {
      return CURRENCY_CODES[country];
    }
  },

  methods: {
    async loadProducts() {
      this.products = [];
      const products = await this.$call('list-giftcards-by-country', { country: this.country });
      for (const product of products)
        product._nameLower = product.name.toLowerCase();
      this.products = products;
    },

    show(product) {
      if (this.searchTerms.length == 0)
        return true;
      for (const term of this.searchTerms)
        if (product._nameLower.includes(term))
          return true;
      return false;
    },

    async select() {
      if (this.existingCountry && (this.country != this.existingCountry))
        throw new VisibleError('Cannot add gift cards from multiple countries to the same order');
      if (!this.amt)
        throw new VisibleError('Please set an amount first');
      const giftcard = {
        amt: parseInt(this.amt),
        cur: this.selected.cur,
        country: this.country,
        src: this.selected.src,
        product_id: this.selected.id,
        product_img: this.selected.img,
        product_name: this.selected.name,
        allowed_values: this.selected.values
      };
      this.selected = null;
      this.$emit('select', giftcard);
      this.$emit('input', false);
    },

    async selectCountry(code) {
      if (this.existingCountry && (code != this.existingCountry))
        throw new VisibleError('Cannot add gift cards from multiple countries to the same order');
      this.country = code;
    },

    showCountryLimitations() {
      this.$confirm({
        title: 'Gift card limitations',
        msg: `These gift cards can only be redeemed in the selected country (${this.country}). We're currently working to support additional countries.`,
        buttons: [ 'Ok' ],
        primary: 'Ok'
      });
    },

    async updatePaymentMetadata() {
      // Make sure the 'amtValid' property has time to update
      await this.$nextTick();
      await this.$nextTick();
      if (this.amt && this.amtValid)
        this.paymentMetadata = {
          target: 'GIFTCARD',
          order_id: this.orderId,
          page_id: this.selectedPageId,
          amt: this.amt,
          country: this.country,
          src: this.selected.src,
          product_id: this.selected.id
        };
    },

    async searchPrivate() {
      const [ provider, id ] = this.searchText.split(':', 2);
      this.selected = await this.$call('get-giftcard', { provider, id });
    }
  },

  watch: {
    country: {
      immediate: true,
      handler(v) {
        if (this.existingCountry && v != this.existingCountry)
          this.country = this.existingCountry;
        else
          this.loadProducts();
      }
    },

    value: {
      immediate: true,
      async handler(v) {
        if (v && !SUPPORTED_COUNTRY_CODES.includes(COUNTRY_CODE)) {
          await this.$nextTick();
          this.$confirm({
            title: 'Gift card limitations',
            msg: `Gift cards cannot be redeemed in your country (${COUNTRY_CODE}). We're currently working to support additional countries.`,
            buttons: [ 'Ok' ],
            primary: 'Ok',
            loading: 3000
          });
        }
      }
    },

    amt: {
      immediate: true,
      handler(v) {
        if (v && /\.\d+$/.test(v))
          this.amt = v.split('.')[0];
        this.updatePaymentMetadata();
      }
    },

    async selected(v) {
      if (!v) {
        this.paymentMetadata = null;
        return;
      }

      // Keep the same 'amt' selected if it's allowed, otherwise select the minimum default
      const amt = parseInt(this.amt || 0);
      if (v.values.range) {
        const max = Math.min(this.maxValue, v.values.range.max);
        if (max < v.values.range.min) {
          this.amtValid = false;
          this.noAmtValid = true;
          return;
        }
        if (amt < v.values.range.min)
          this.amt = String(v.values.range.min);
        else if (amt > max)
          this.amt = String(max);
      } else {
        if (!v.values.fixed.includes(amt)) {
          let min = 99999999;
          for (const f of v.values.fixed) {
            const d = amt - f;
            if (Math.abs(d) < Math.abs(min))
              min = d;
          }
          const minAmt = String(amt - min);
          if (minAmt > this.maxValue) {
            this.amtValid = false;
            this.noAmtValid = true;
            return;
          }
          this.amt = minAmt;
        }
      }

      await this.$nextTick();
      this.amtValid = true;
      this.noAmtValid = false;
      this.updatePaymentMetadata();
    }
  }
}
</script>
